import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useCmsThemeConfig } from '@/hooks/useCmsThemeConfig';
import { CmsThemeConfig, DEFAULT_THEME_CONFIG, TYPOGRAPHY_PACKS, ACCENT_VARIATIONS, getDesignSystemPreset } from '@/config/cmsThemePresets';
import { useContentMode } from '@/hooks/useContentMode';
import { useTheme } from '@/context/ThemeContext';

interface CmsThemeContextValue {
  config: CmsThemeConfig;
  isLoading: boolean;
  isHydraMode: boolean;
  /** @deprecated Use isHydraMode instead */
  isCmsMode: boolean;
}

const CmsThemeContext = createContext<CmsThemeContextValue | undefined>(undefined);

/**
 * Convert a hex color to HSL string (e.g. "210 50% 40%")
 */
function hexToHsl(hex: string): string {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function deriveLightBg(hsl: string): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return '0 0% 98%';
  return `${parts[1]} ${Math.min(parseInt(parts[2]), 30)}% 97%`;
}

function deriveDarkBg(hsl: string): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return '0 0% 4%';
  return `${parts[1]} ${Math.min(parseInt(parts[2]), 25)}% 4%`;
}

function deriveFuturisticBg(hsl: string): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return '230 25% 5%';
  return `${parts[1]} ${Math.min(parseInt(parts[2]), 25)}% 5%`;
}

function deriveMuted(hsl: string, isDark: boolean): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return isDark ? '0 0% 10%' : '0 0% 96%';
  return isDark
    ? `${parts[1]} ${Math.min(parseInt(parts[2]), 15)}% 12%`
    : `${parts[1]} ${Math.min(parseInt(parts[2]), 20)}% 95%`;
}

function deriveForeground(hsl: string, isDark: boolean): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return isDark ? '0 0% 96%' : '0 0% 10%';
  return isDark
    ? `${parts[1]} ${Math.min(parseInt(parts[2]), 10)}% 96%`
    : `${parts[1]} ${Math.min(parseInt(parts[2]), 30)}% 10%`;
}

function deriveMutedForeground(hsl: string, isDark: boolean): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return isDark ? '0 0% 50%' : '0 0% 32%';
  return isDark
    ? `${parts[1]} ${Math.min(parseInt(parts[2]), 10)}% 50%`
    : `${parts[1]} ${Math.min(parseInt(parts[2]), 30)}% 32%`;
}

function deriveCard(hsl: string, isDark: boolean, isFuturistic: boolean): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return isDark ? '0 0% 6%' : '0 0% 100%';
  if (isFuturistic) return `${parts[1]} ${Math.min(parseInt(parts[2]), 25)}% 7%`;
  return isDark
    ? `${parts[1]} ${Math.min(parseInt(parts[2]), 15)}% 6%`
    : `${parts[1]} ${Math.min(parseInt(parts[2]), 10)}% 100%`;
}

function deriveBorder(hsl: string, isDark: boolean): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return isDark ? '0 0% 14%' : '0 0% 90%';
  return isDark
    ? `${parts[1]} ${Math.min(parseInt(parts[2]), 15)}% 16%`
    : `${parts[1]} ${Math.min(parseInt(parts[2]), 15)}% 90%`;
}

function derivePrimaryForeground(hsl: string): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%?\s+(\d+)%?/);
  if (!parts) return '0 0% 100%';
  const l = parseInt(parts[3]);
  return l > 50 ? `${parts[1]} ${parts[2]}% 10%` : `${parts[1]} ${Math.min(parseInt(parts[2]), 30)}% 98%`;
}

/** Determine the CSS @font-face format string from a font file URL */
function getFontFormat(url: string): string {
  const lower = url.toLowerCase();
  if (lower.endsWith('.woff2')) return 'woff2';
  if (lower.endsWith('.woff')) return 'woff';
  if (lower.endsWith('.ttf')) return 'truetype';
  if (lower.endsWith('.otf')) return 'opentype';
  return 'woff2'; // safe default
}

/** All CSS custom properties that brand colors should override */
const BRAND_PROPERTIES = [
  'primary', 'primary-foreground',
  'secondary', 'secondary-foreground',
  'accent', 'accent-foreground',
  'background', 'foreground',
  'card', 'card-foreground',
  'muted', 'muted-foreground',
  'border', 'input',
  'ring',
] as const;

/** Design system token properties */
const DS_PROPERTIES = [
  '--ds-radius', '--ds-shadow', '--ds-shadow-hover',
  '--ds-border', '--ds-blur', '--ds-surface', '--ds-surface-hover',
  '--ds-inset-shadow',
] as const;

/** Extra custom properties to clean up */
const EXTRA_PROPERTIES = [
  '--radius', '--card', '--card-foreground', '--border', '--input',
  '--cms-accent', '--cms-dark', '--cms-light', '--cms-heading-font', '--cms-body-font',
] as const;

/**
 * Provides the active CMS theme configuration to all themed components.
 * Brand colors are applied via element.style.setProperty so they always
 * override [data-theme] selectors from index.css (inline > attribute selectors).
 */
export const CmsThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { themeConfig, isLoading: isThemeConfigLoading } = useCmsThemeConfig();
  const { isHydraMode, isLoading: isContentModeLoading } = useContentMode();
  const { theme } = useTheme();
  const prevApplied = useRef(false);
  const [themeReady, setThemeReady] = React.useState(false);

  // Track whether we're on an admin route — check both the URL path AND body class.
  // URL check works on first render before AdminDashboard mounts; body class is a backup.
  const isAdminRoute = () =>
    typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/admin') ||
      document.body.classList.contains('admin-active')
    );

  // Re-render when admin-active class changes on <body> so the layout effect re-runs
  const [adminToggleCount, setAdminToggleCount] = React.useState(0);
  React.useEffect(() => {
    const observer = new MutationObserver(() => setAdminToggleCount(n => n + 1));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const config = isHydraMode ? themeConfig : DEFAULT_THEME_CONFIG;

  // Resolve colors — all palettes are brand-based with accent variations
  const typoPack = TYPOGRAPHY_PACKS.find(t => t.value === config.typography_pack);
  const accentVariation = ACCENT_VARIATIONS.find(v => v.value === config.color_palette);

  const headingFont = config.custom_fonts?.heading || typoPack?.heading || 'Oswald';
  const bodyFont = config.custom_fonts?.body || typoPack?.body || 'Open Sans';
  const headingFileUrl = config.custom_fonts?.headingFileUrl || null;
  const bodyFileUrl = config.custom_fonts?.bodyFileUrl || null;

  // Primary & secondary always come from extracted brand colors
  const primaryHsl = config.custom_colors?.primary || '24 95% 53%';
  const secondaryHsl = config.custom_colors?.secondary || '20 6% 10%';
  
  // Accent: use variation's preset accent, or fall back to brand's original accent
  let accentHsl: string;
  if (config.color_palette === 'brand' || !accentVariation?.accentHsl) {
    accentHsl = config.custom_colors?.accent || '27 96% 83%';
  } else {
    accentHsl = accentVariation.accentHsl;
  }

  // Hydra mode always renders as light for a clean professional look
  // regardless of the theme switcher selection
  const isDark = isHydraMode ? false : (theme === 'glassmorphism' || theme === 'futuristic');
  const isFuturistic = isHydraMode ? false : theme === 'futuristic';

  // Resolve design system tokens
  const dsPreset = getDesignSystemPreset(config.design_system || 'modern');

  // Build the full set of CSS custom properties
  // MONOCHROME EDITORIAL: Only --primary uses brand color. Everything else stays grayscale.
  // Design system tokens (--ds-*) control visual language (shadows, borders, radius, blur).
  const brandProps = React.useMemo(() => {
    if (!isHydraMode) return null;

    // For neumorphic, shift background & muted to match so extrusions look correct
    const isNeumorphic = config.design_system === 'neumorphic';
    const bgVal = isNeumorphic ? '0 0% 94%' : '0 0% 100%';
    const mutedVal = isNeumorphic ? '0 0% 94%' : '0 0% 96%';

    return {
      '--primary': primaryHsl,
      '--primary-foreground': derivePrimaryForeground(primaryHsl),
      '--secondary': '0 0% 96%',
      '--secondary-foreground': '0 0% 10%',
      '--accent': '0 0% 96%',
      '--accent-foreground': '0 0% 5%',
      '--background': bgVal,
      '--foreground': '0 0% 5%',
      '--card': isNeumorphic ? '0 0% 94%' : '0 0% 100%',
      '--card-foreground': '0 0% 5%',
      '--muted': mutedVal,
      '--muted-foreground': '0 0% 45%',
      '--border': '0 0% 90%',
      '--input': '0 0% 90%',
      '--ring': primaryHsl,
      '--radius': dsPreset.tokens['--ds-radius'],
      '--cms-accent': `hsl(${primaryHsl})`,
      '--cms-dark': `hsl(${secondaryHsl})`,
      '--cms-light': `hsl(${accentHsl})`,
      '--cms-heading-font': `'${headingFont}', 'Space Grotesk', sans-serif`,
      '--cms-body-font': `'${bodyFont}', 'Inter', sans-serif`,
      // Design system tokens
      ...dsPreset.tokens,
    } as Record<string, string>;
  }, [isHydraMode, primaryHsl, secondaryHsl, accentHsl, headingFont, bodyFont, dsPreset, config.design_system]);

  // Apply brand colors SYNCHRONOUSLY via useLayoutEffect to prevent FOUC on navigation.
  // In admin mode: set NOTHING on :root — the admin's own [data-admin-theme] CSS handles everything.
  // On the public site: set ALL properties (colors, fonts, design system tokens).
  React.useLayoutEffect(() => {
    const el = document.documentElement;
    const inAdmin = isAdminRoute();

    // Always clean up first to ensure no stale properties linger
    if (prevApplied.current) {
      BRAND_PROPERTIES.forEach(p => el.style.removeProperty(`--${p}`));
      DS_PROPERTIES.forEach(p => el.style.removeProperty(p));
      EXTRA_PROPERTIES.forEach(p => el.style.removeProperty(p));
      el.style.removeProperty('--radius');
    }

    if (brandProps && !inAdmin) {
      // ── PUBLIC SITE ONLY ──
      // Apply everything — colors, fonts, design system tokens
      Object.entries(brandProps).forEach(([key, val]) => {
        el.style.setProperty(key, val);
      });
      prevApplied.current = true;
    } else {
      prevApplied.current = false;
    }

    return () => {
      if (prevApplied.current) {
        BRAND_PROPERTIES.forEach(p => el.style.removeProperty(`--${p}`));
        DS_PROPERTIES.forEach(p => el.style.removeProperty(p));
        EXTRA_PROPERTIES.forEach(p => el.style.removeProperty(p));
        el.style.removeProperty('--radius');
        prevApplied.current = false;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandProps, adminToggleCount]);

  // Mark theme as ready once all queries have resolved
  const isLoading = isThemeConfigLoading || isContentModeLoading;
  React.useEffect(() => {
    if (!isLoading) {
      // Use rAF to ensure styles are painted before revealing content
      requestAnimationFrame(() => setThemeReady(true));
    }
  }, [isLoading]);

  return (
    <CmsThemeContext.Provider value={{ config, isLoading, isHydraMode, isCmsMode: isHydraMode }}>
      {isHydraMode && (
        <style>{`
          ${headingFileUrl ? `
            @font-face {
              font-family: '${headingFont}';
              src: url('${headingFileUrl}') format('${getFontFormat(headingFileUrl)}');
              font-weight: 100 900;
              font-display: swap;
            }
          ` : ''}
          ${bodyFileUrl ? `
            @font-face {
              font-family: '${bodyFont}';
              src: url('${bodyFileUrl}') format('${getFontFormat(bodyFileUrl)}');
              font-weight: 100 900;
              font-display: swap;
            }
          ` : ''}
          ${/* Google Fonts import for any font NOT using an uploaded file */''}
          ${!headingFileUrl || !bodyFileUrl ? `
            @import url('https://fonts.googleapis.com/css2?${!headingFileUrl ? `family=${headingFont.replace(/ /g, '+')}:wght@400;600;700;800;900` : ''}${!headingFileUrl && !bodyFileUrl ? '&' : ''}${!bodyFileUrl ? `family=${bodyFont.replace(/ /g, '+')}:wght@400;500;600` : ''}&display=swap');
          ` : ''}
        `}</style>
      )}
      {/* Hide children until theme CSS vars are applied to prevent FOUC */}
      <div style={{ visibility: themeReady ? 'visible' : 'hidden' }}>
        {children}
      </div>
    </CmsThemeContext.Provider>
  );
};

export const useCmsTheme = () => {
  const ctx = useContext(CmsThemeContext);
  if (!ctx) throw new Error('useCmsTheme must be used within CmsThemeProvider');
  return ctx;
};
